export const ADMIN_SECTIONS = [
  ['overview', 'Overview'],
  ['beta-evidence', 'Beta Evidence'],
  ['ask-pinngro-usage', 'Ask Pinngro Usage'],
  ['users', 'Users'],
  ['subscriptions', 'Subscriptions'],
  ['bots', 'Bots & Activity'],
  ['oauth', 'OAuth/Broker Health'],
  ['backtests', 'Backtests'],
  ['market-data', 'Market Data & Workers'],
  ['system-health', 'System Health'],
  ['audit', 'Audit Log'],
  ['admin-audit', 'Admin Actions'],
];

export const adminErrorMessage = (error) => {
  if (error?.response?.status === 403) return 'Admin access is required.';
  if (error?.response?.status === 404) return 'The requested record is unavailable.';
  if ([409, 422].includes(error?.response?.status)) return 'The action was not applied. Check the confirmation and current account state.';
  if (error?.response?.status === 503) return 'Administrative data is temporarily unavailable.';
  return 'The administrative view could not be loaded.';
};

export const formatDateTime = (value) => {
  if (!value) return 'Not recorded';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 'Unavailable' : parsed.toLocaleString();
};

export const formatLastLogin = (value) => {
  if (!value) return 'Never recorded';
  return formatDateTime(value);
};

export const formatNumber = (value, fallback = '0') => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric.toLocaleString() : fallback;
};

export const formatPercent = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? `${numeric.toFixed(2)}%` : '—';
};

export const shortReference = (value, length = 10) => (
  value ? `${String(value).slice(0, length)}…` : 'Not available'
);

export const titleCase = (value) => String(value || 'unknown')
  .replaceAll('_', ' ')
  .replace(/\b\w/g, (character) => character.toUpperCase());

export const statusTone = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (['healthy', 'healthy_fresh', 'current', 'active', 'trialing', 'premium', 'completed', 'succeeded', 'connected_recorded', 'stored_connection'].includes(normalized)) {
    return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300';
  }
  if (['running', 'due', 'queued', 'ready', 'paper', 'basic', 'idle_market_closed', 'no_active_bots', 'degraded_prefetch', 'matched_release', 'authorized_split'].includes(normalized)) {
    return 'bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300';
  }
  if (['critical', 'expired', 'rollback_required'].includes(normalized)) {
    return 'bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-300';
  }
  if (['warning', 'danger', 'unexpected_mismatch', 'needs_attention', 'needs_connection', 'connect_alpaca', 'reconnect_required', 'scheduler_failed', 'monitor_failed', 'provider_failed', 'generation_failed', 'quota_blocked', 'stale', 'missing', 'failed', 'error', 'downgraded', 'partial', 'suspended', 'evaluation_overdue', 'evaluation_failed', 'worker_missing', 'reconciler_failed', 'acquisition_overdue', 'acquisition_failed', 'stale_market_data'].includes(normalized)) {
    return 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300';
  }
  return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';
};

export const safeRows = (data, key) => {
  const value = key ? data?.[key] : data;
  return Array.isArray(value) ? value : [];
};

export const adminEndpoints = {
  overview: '/api/admin/overview',
  'beta-evidence': '/api/admin/beta-evidence',
  'ask-pinngro-usage': '/api/admin/ask-pinngro-usage',
  users: '/api/admin/users',
  subscriptions: '/api/admin/subscriptions',
  bots: '/api/admin/bots',
  oauth: '/api/admin/oauth-health',
  backtests: '/api/admin/backtests',
  'market-data': '/api/admin/market-data-workers',
  'system-health': '/api/admin/system-health',
  audit: '/api/admin/audit-log',
  'admin-audit': '/api/admin/action-audit',
};

export const OAUTH_FILTERS = [
  ['relevant', 'Relevant'], ['connected', 'Connected'],
  ['needs-connection', 'Needs connection'], ['all', 'All accounts'],
];

export const BOT_FILTERS = [
  ['relevant', 'Active & needs attention'], ['ready', 'Ready'],
  ['watching', 'Watching'], ['draft', 'Draft'], ['paused', 'Paused'],
  ['archived', 'Archived'], ['all', 'All'],
];

export const MARKET_DATA_FILTERS = [
  ['relevant', 'Relevant'], ['needs-attention', 'Needs attention'],
  ['demanded', 'Demanded'], ['inactive', 'Inactive'], ['all', 'All'],
];

const lexical = (left, right, fields) => {
  for (const field of fields) {
    const compared = String(left?.[field] ?? '').localeCompare(String(right?.[field] ?? ''));
    if (compared) return compared;
  }
  return 0;
};

export const filterAndSortOAuthConnections = (rows, filter = 'relevant') => {
  const filtered = (Array.isArray(rows) ? rows : []).filter((row) => {
    if (filter === 'connected') return row.connection_state === 'stored_connection';
    if (filter === 'needs-connection') return row.attention_state === 'needs_connection';
    if (filter === 'all') return true;
    return row.relevant === true;
  });
  const priority = (row) => (
    row.attention_state === 'reconnect_required' ? 0
      : row.attention_state === 'needs_connection' ? 1
        : row.connection_state === 'stored_connection' ? 2 : 3
  );
  return filtered.sort((left, right) => priority(left) - priority(right)
    || lexical(left, right, ['username', 'user_id']));
};

export const filterAndSortBots = (rows, filter = 'relevant') => {
  const source = Array.isArray(rows) ? rows : [];
  const displayState = (row) => String(row.canonical_display_state || row.status || '').toLowerCase();
  const isWatching = (row) => displayState(row) === 'watching';
  const isAttention = (row) => row.needs_attention === true
    || ['needs_attention', 'connect_alpaca'].includes(displayState(row));
  const relevant = (row) => row.status !== 'archived' && (
    isAttention(row) || isWatching(row) || row.desired_state === 'run'
      || ['ready', 'draft'].includes(displayState(row))
      || (displayState(row) === 'paused'
        && (row.has_open_position || row.pending_proposal_count > 0))
  );
  const filtered = source.filter((row) => {
    if (filter === 'all') return true;
    if (filter === 'relevant') return relevant(row);
    if (filter === 'watching') return isWatching(row) && row.status !== 'archived';
    return displayState(row) === filter;
  });
  const priority = (row) => isAttention(row) ? 0
    : isWatching(row) ? 1
      : displayState(row) === 'ready' ? 2
        : displayState(row) === 'draft' ? 3
          : displayState(row) === 'paused' ? 4
            : displayState(row) === 'archived' ? 6 : 5;
  return filtered.sort((left, right) => priority(left) - priority(right)
    || lexical(left, right, ['name', 'id']));
};

export const filterAndSortPrefetchJobs = (rows, filter = 'relevant') => {
  const source = Array.isArray(rows) ? rows : [];
  const needsAttention = (row) => row.stale_claim
    || ['failed', 'partial', 'cooldown'].includes(String(row.status).toLowerCase());
  const demanded = (row) => Number(row.demand_count || 0) > 0;
  const active = (row) => ['pending', 'running'].includes(String(row.status).toLowerCase());
  const filtered = source.filter((row) => {
    if (filter === 'all') return true;
    if (filter === 'needs-attention') return needsAttention(row);
    if (filter === 'demanded') return demanded(row);
    if (filter === 'inactive') return !demanded(row) && !active(row) && !needsAttention(row);
    return demanded(row) || active(row) || needsAttention(row);
  });
  const priority = (row) => needsAttention(row) ? 0 : demanded(row) ? 1 : active(row) ? 2 : 3;
  return filtered.sort((left, right) => priority(left) - priority(right)
    || lexical(left, right, ['symbol', 'id']));
};

export const operationalHealthPriority = (value) => {
  const normalized = String(value || '').toLowerCase();
  if (['failed', 'stale', 'missing', 'critical'].includes(normalized)) return 0;
  if (['needs_attention', 'warning', 'overdue', 'partial'].includes(normalized)) return 1;
  if (['healthy', 'watching', 'active', 'running'].includes(normalized)) return 2;
  if (['idle', 'inactive', 'no_active_bots', 'idle_market_closed'].includes(normalized)) return 3;
  return 1;
};

export const frontendBuildCommit = import.meta.env?.VITE_PINNGRO_BUILD_COMMIT || 'unavailable';

export const adminReleaseObservationConfig = () => ({
  headers: { 'X-Pinngro-Frontend-Build': frontendBuildCommit },
});

export const ADMIN_USER_ACTIONS = {
  suspend: {
    label: 'Suspend account',
    consequence: 'Blocks application access, revokes existing sessions, and stops owned practice bot workers. Data, billing, OAuth, and history are preserved.',
    tone: 'danger',
  },
  reactivate: {
    label: 'Reactivate account',
    consequence: 'Restores application access. Previously stopped practice bots are not restarted.',
    tone: 'safe',
  },
  'revoke-sessions': {
    label: 'Revoke sessions',
    consequence: 'Invalidates all current Pinngro application sessions for this user only. OAuth connections are unchanged.',
    tone: 'warning',
  },
  'stop-practice-bots': {
    label: 'Stop practice bots',
    consequence: 'Sets only this user’s active paper worker controls to stop. No broker or live-trading action is performed.',
    tone: 'warning',
  },
  'promote-admin': {
    label: 'Make Admin',
    consequence: 'Grants access to Human Beta user administration and operational Admin information.',
    tone: 'warning',
  },
  'demote-admin': {
    label: 'Remove Admin access',
    consequence: 'Removes Admin access. The server prevents self-demotion and protects the final active Admin.',
    tone: 'danger',
  },
};

export const roleActionPresentation = (user, capabilities) => {
  const isAdmin = user?.is_admin === true;
  const actionKey = isAdmin ? 'demote-admin' : 'promote-admin';
  const allowedKey = isAdmin ? 'can_demote_admin' : 'can_promote_admin';
  const blockersKey = isAdmin ? 'demote_blockers' : 'promote_blockers';
  const guidanceKey = isAdmin ? 'demote_guidance' : 'promote_guidance';
  const validContract = capabilities?.contract === 'admin-role-capabilities-v1';
  const blockers = validContract && Array.isArray(capabilities?.[blockersKey])
    ? capabilities[blockersKey] : ['CAPABILITY_UNAVAILABLE'];
  const enabled = validContract
    && capabilities?.[allowedKey] === true
    && blockers.length === 0;
  return {
    actionKey,
    enabled,
    blockers,
    guidance: enabled ? null : capabilities?.[guidanceKey]
      || 'Unavailable — current role eligibility could not be confirmed.',
  };
};

export const USER_FILTERS = [
  ['active', 'Active Beta'],
  ['onboarding', 'Needs onboarding'],
  ['suspended', 'Suspended'],
  ['admins', 'Admins'],
  ['invitation', 'Invitation pending'],
  ['all', 'All'],
];

export const filterAndSortUsers = (rows, filter, search = '') => {
  const query = String(search).trim().toLowerCase();
  const source = [...(Array.isArray(rows) ? rows : [])];
  const matches = (row) => !query || [row.username, row.email]
    .some((value) => String(value || '').toLowerCase().includes(query));
  const included = (row) => {
    if (filter === 'all') return true;
    if (filter === 'suspended') return Boolean(row.is_suspended);
    if (filter === 'admins') return Boolean(row.is_admin);
    if (filter === 'invitation') return row.invitation_state === 'ACTIVE';
    if (filter === 'onboarding') return !row.is_verified || !row.is_2fa_enabled;
    return !row.is_suspended
      && ['OPEN', 'LEGACY_OPEN'].includes(String(row.lifecycle_state || 'LEGACY_OPEN'))
      && row.is_verified;
  };
  const attention = (row) => (
    row.is_suspended || !row.is_verified || !row.is_2fa_enabled
      || Number(row.needs_attention_count || 0) > 0 ? 0 : 1
  );
  return source.filter((row) => matches(row) && included(row)).sort((left, right) => (
    attention(left) - attention(right)
    || String(left.username || '').localeCompare(String(right.username || ''))
    || Number(left.id) - Number(right.id)
  ));
};
